/*
 * Unified transfer-log table — replaces the prior split between
 * "Sync activity" (jobs) and "Forwarding" (runs). One timeline of every
 * data-movement event with filters, status pills, and a row-detail
 * drawer for failure triage.
 *
 * Single component used by both consoles:
 *   - Workspace path /app/data-activity/transfer-logs   → scope="workspace"
 *   - Admin path     /admin/sync/transfer-logs          → scope="platform"
 *
 * Platform scope respects the ?workspaceId= query param for cross-tenant
 * narrowing (writer is the WorkspaceSelector in the header).
 */

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, ApiError } from "../lib/api.js";
import { SkeletonTable } from "../components/Skeleton.js";
import StatusBadge from "../components/StatusBadge.js";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";

type Transfer = Awaited<ReturnType<typeof api.listTransfers>>["transfers"][number];

const TIME_WINDOWS: Array<{ id: string; label: string; ms: number | null }> = [
  { id: "1h",  label: "Last hour",  ms: 60 * 60 * 1000 },
  { id: "24h", label: "Last 24 h",  ms: 24 * 60 * 60 * 1000 },
  { id: "7d",  label: "Last 7 d",   ms: 7 * 24 * 60 * 60 * 1000 },
  { id: "all", label: "All time",   ms: null }
];
const STATUS_OPTIONS = ["all", "queued", "running", "completed", "succeeded", "failed", "dead-lettered", "pending"];
const SOURCE_OPTIONS = ["all", "sync-job", "forwarding-run"];

function statusTone(status: string): string {
  if (status === "completed" || status === "succeeded") return "ok";
  if (status === "failed" || status === "dead-lettered") return "err";
  if (status === "running" || status === "pending") return "info";
  return "muted";
}

function relTime(iso?: string): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const delta = Date.now() - t;
  const s = Math.floor(delta / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function durationLabel(start: string, end?: string): string {
  if (!end) return "—";
  const ms = Date.parse(end) - Date.parse(start);
  if (!Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

export interface TransferLogsProps {
  scope: "workspace" | "platform";
}

export default function TransferLogs({ scope }: TransferLogsProps) {
  const [searchParams] = useSearchParams();
  const [transfers, setTransfers] = useState<Transfer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [windowId, setWindowId] = useState<string>("24h");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Transfer | null>(null);

  // Platform scope honours the global WorkspaceSelector via ?workspaceId.
  const workspaceFilter = scope === "platform" ? searchParams.get("workspaceId") ?? undefined : undefined;

  useEffect(() => {
    let cancelled = false;
    const win = TIME_WINDOWS.find((w) => w.id === windowId);
    const sinceMs = win?.ms ? Date.now() - win.ms : undefined;
    api.listTransfers({
      workspaceId: workspaceFilter,
      status: statusFilter === "all" ? undefined : statusFilter,
      source: sourceFilter === "all" ? undefined : sourceFilter,
      sinceMs
    })
      .then((r) => { if (!cancelled) setTransfers(r.transfers); })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [windowId, statusFilter, sourceFilter, workspaceFilter]);

  const filtered = useMemo(() => {
    if (!transfers) return [];
    if (!search.trim()) return transfers;
    const q = search.toLowerCase();
    return transfers.filter((t) =>
      [t.id, t.type, t.environment, t.destinationId, t.message, t.error, t.workspaceId]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(q))
    );
  }, [transfers, search]);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Transfer logs</h1>
          <p className="muted">
            Unified view of every data-movement event — sync jobs and forwarding runs — across {scope === "platform" ? "all workspaces" : "this workspace"}.
            Each row drills into its full detail for failure triage.
          </p>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error" role="alert">{error}</div>}

      <section className="transfer-filters" aria-label="Transfer log filters">
        <label className="transfer-filter">
          Window
          <select value={windowId} onChange={(e) => setWindowId(e.target.value)}>
            {TIME_WINDOWS.map((w) => <option key={w.id} value={w.id}>{w.label}</option>)}
          </select>
        </label>
        <label className="transfer-filter">
          Source
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
            {SOURCE_OPTIONS.map((s) => <option key={s} value={s}>{s === "all" ? "All sources" : s}</option>)}
          </select>
        </label>
        <label className="transfer-filter">
          Status
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s === "all" ? "All statuses" : s}</option>)}
          </select>
        </label>
        <label className="transfer-filter transfer-filter-grow">
          Search
          <input
            type="search"
            placeholder="id / type / environment / destination / error…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </section>

      {transfers === null && <SkeletonTable rows={6} columns={scope === "platform" ? 9 : 8} />}
      {transfers !== null && filtered.length === 0 && (
        <div className="empty-state-card">
          <h2 style={{ marginTop: 0 }}>No transfer events match your filters</h2>
          <p>
            Trigger a metadata sync, run an ingestion job, or schedule a forwarding run — every
            attempt produces a transfer log row whether it succeeds or fails.
          </p>
        </div>
      )}

      {filtered.length > 0 && (
        <table className="transfer-table">
          <thead>
            <tr>
              <th>Started</th>
              <th>Source</th>
              <th>Type</th>
              {scope === "platform" && <th>Workspace</th>}
              <th>Environment / Destination</th>
              <th>Status</th>
              <th style={{ textAlign: "right" }}>Items</th>
              <th>Duration</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr key={t.id} className={selected?.id === t.id ? "active" : undefined}>
                <td title={t.startedAt}>{relTime(t.startedAt)}</td>
                <td><span className="transfer-source">{t.source}</span></td>
                <td className="mono" style={{ fontSize: 12 }}>{t.type}</td>
                {scope === "platform" && <td className="mono" style={{ fontSize: 11 }}>{t.workspaceId ?? "—"}</td>}
                <td className="mono" style={{ fontSize: 11 }}>{t.environment ?? t.destinationId ?? "—"}</td>
                <td><StatusBadge status={t.status} /></td>
                <td style={{ textAlign: "right" }}>{t.itemCount ?? "—"}</td>
                <td>{durationLabel(t.startedAt, t.completedAt)}</td>
                <td>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelected(t)}>
                    Detail →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected && (
        <aside className="transfer-detail-drawer" role="dialog" aria-label="Transfer detail">
          <header>
            <h2>Transfer detail</h2>
            <button type="button" onClick={() => setSelected(null)} aria-label="Close">×</button>
          </header>
          <dl>
            <dt>ID</dt><dd className="mono" style={{ fontSize: 12 }}>{selected.id}</dd>
            <dt>Source</dt><dd>{selected.source}</dd>
            <dt>Type</dt><dd>{selected.type}</dd>
            {selected.workspaceId && (<><dt>Workspace</dt><dd className="mono" style={{ fontSize: 12 }}>{selected.workspaceId}</dd></>)}
            {selected.environment && (<><dt>Environment</dt><dd>{selected.environment}</dd></>)}
            {selected.destinationId && (<><dt>Destination</dt><dd className="mono" style={{ fontSize: 12 }}>{selected.destinationId}</dd></>)}
            <dt>Status</dt><dd><StatusBadge status={selected.status} /></dd>
            <dt>Started</dt><dd>{selected.startedAt}</dd>
            <dt>Completed</dt><dd>{selected.completedAt ?? "—"}</dd>
            <dt>Items</dt><dd>{selected.itemCount ?? "—"}</dd>
            {selected.message && (<><dt>Message</dt><dd>{selected.message}</dd></>)}
            {selected.error && (<><dt>Error</dt><dd className="error-text">{selected.error}</dd></>)}
            {selected.correlationId && (<><dt>Correlation ID</dt><dd className="mono" style={{ fontSize: 12 }}>{selected.correlationId}</dd></>)}
          </dl>
        </aside>
      )}
    </div>
  );
}
